/**
 * The theme is two choices — mode and accent — and nothing else. Everything a
 * component paints with is derived from them in `tokens.css`, so applying a theme
 * is setting two data attributes on the document element, plus one custom property
 * when the accent is a hex the listener typed rather than one of the approved set.
 */
export type ThemeMode = "dark" | "light";

export const APPROVED_ACCENTS = {
  amber: { label: "Amber", value: "oklch(0.78 0.14 68)" },
  ember: { label: "Ember", value: "oklch(0.70 0.17 35)" },
  teal: { label: "Teal", value: "oklch(0.74 0.11 195)" },
  cyan: { label: "Cyan", value: "oklch(0.80 0.11 225)" },
  violet: { label: "Violet", value: "oklch(0.72 0.15 295)" },
  rose: { label: "Rose", value: "oklch(0.70 0.16 15)" },
} as const;

export type AccentId = keyof typeof APPROVED_ACCENTS;
export type AccentChoice = AccentId | "custom";

/** On-air amber. A radio identity, not the reference tool's. */
export const DEFAULT_ACCENT: AccentId = "amber";
export const DEFAULT_MODE: ThemeMode = "dark";
export const DEFAULT_CUSTOM_ACCENT = "#f0a35a";

/** Read by the pre-paint script in `index.html`. Changing it changes both. */
export const THEME_STORAGE_KEY = "radio.theme";

export interface Theme {
  mode: ThemeMode;
  accent: AccentChoice;
  customAccent: string;
}

export const DEFAULT_THEME: Theme = {
  mode: DEFAULT_MODE,
  accent: DEFAULT_ACCENT,
  customAccent: DEFAULT_CUSTOM_ACCENT,
};

/** What the browser chrome is told, so the notch matches the surface behind it. */
const THEME_COLORS: Record<ThemeMode, string> = {
  dark: "#212225",
  light: "#eeeff2",
};

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === "dark" || value === "light";
}

export function isAccentId(value: unknown): value is AccentId {
  return typeof value === "string" && Object.hasOwn(APPROVED_ACCENTS, value);
}

export function isHexColor(value: unknown): value is string {
  return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);
}

/**
 * Every field is validated on the way out of storage. A half-written, hand-edited
 * or version-skewed entry degrades to the default for that field alone rather than
 * throwing away the listener's other choices.
 */
export function readStoredTheme(storage?: Storage | undefined): Theme {
  const store = storage ?? safeLocalStorage();
  if (!store) return DEFAULT_THEME;
  try {
    const raw: unknown = JSON.parse(store.getItem(THEME_STORAGE_KEY) ?? "{}");
    if (typeof raw !== "object" || raw === null) return DEFAULT_THEME;
    const stored = raw as Record<string, unknown>;
    return {
      mode: isThemeMode(stored.mode) ? stored.mode : DEFAULT_THEME.mode,
      accent:
        stored.accent === "custom" || isAccentId(stored.accent)
          ? stored.accent
          : DEFAULT_THEME.accent,
      customAccent: isHexColor(stored.customAccent)
        ? stored.customAccent.toLowerCase()
        : DEFAULT_THEME.customAccent,
    };
  } catch {
    return DEFAULT_THEME;
  }
}

export function writeStoredTheme(
  theme: Theme,
  storage?: Storage | undefined,
): void {
  const store = storage ?? safeLocalStorage();
  if (!store) return;
  try {
    store.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
  } catch {
    // Storage can be refused outright in hardened contexts. The chosen theme still
    // applies for this session; it simply will not survive a reload.
  }
}

/**
 * Applying a theme touches the document element and nothing else. An approved
 * accent is named, never inlined, so `tokens.css` stays the only place a colour is
 * written; a custom accent is the one value that cannot be known ahead of time, and
 * even then only `--accent` is set — its ink is derived from it in CSS.
 */
export function applyTheme(root: HTMLElement, theme: Theme): void {
  root.dataset.theme = theme.mode;
  root.dataset.accent = theme.accent;
  if (theme.accent === "custom") {
    root.style.setProperty("--accent", theme.customAccent);
  } else {
    root.style.removeProperty("--accent");
  }
  const meta = root.ownerDocument.querySelector('meta[name="theme-color"]');
  meta?.setAttribute("content", THEME_COLORS[theme.mode]);
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}
